import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDownTrayIcon, CircleStackIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ButtonLinkList from '@ui/button/ButtonLinkList';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import ChartCard from '@ui/chart/ChartCard';
import BarChart, { BarChartBand, BarChartMarker, BarChartSeries } from '@ui/chart/BarChart';
import LineChart from '@ui/chart/LineChart';
import DataTable, { DataTableColumn, DataTableRow } from '@ui/chart/DataTable';
import ChartMessage from '@ui/chart/ChartMessage';
import FormButton from '@app/component/form/button/FormButton';
import { CHART_HEIGHT } from '@ui/chart/chartTokens';
import { ValueFormat } from '@ui/chart/chartFormat';
import { EvolutionGrain, EvolutionMetric, RevenueScope, SegmentationView, VatBasis } from '@app/model/bi/Bi';
import { useSegmentEvolution } from '@app/model/bi/service/BiHooks';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { formatMonthRange, MonthRange } from '@ui/selector/MonthRangePicker';
import { evolutionFileName, formatDate, missingNote, missingOf, monthsIn, revenueName, WEEK_MAX_MONTHS } from '../Bi.utilities';
import { figuresOf, Metric, tableOf } from './SegmentEvolution.utilities';

const METRICS: { value: Metric, label: string, format: ValueFormat, decimals: number, input: EvolutionMetric }[] = [
	{ value: 'roomNights', label: 'Room nights', format: 'number', decimals: 0, input: 'ROOM_NIGHTS' },
	{ value: 'revenue', label: 'Revenue', format: 'money', decimals: 0, input: 'REVENUE' },
	{ value: 'adr', label: 'ADR', format: 'money', decimals: 2, input: 'ADR' },
	{ value: 'mix', label: 'Mix %', format: 'percent', decimals: 1, input: 'MIX' },
];
const GRAINS: { value: EvolutionGrain, label: string }[] = [{ value: 'MONTH', label: 'Month' }, { value: 'WEEK', label: 'Week' }];
type Props = {
	companyHotelId: number | null,
	// The Property group of the page (then companyHotelId is null).
	propertyGroupId: number | null,
	// false until the page knows its selection: a ?group= link waits for the Property groups.
	ready: boolean,
	// ROO2-310: the Options switch of the page.
	includeOptions: boolean,
	// The stay months of the page: the one picker above the chart and the table.
	period: MonthRange,
	view: SegmentationView,
	// The revenue switches of the page (ROO2-236): they select the revenue and the ADR.
	scope: RevenueScope,
	vat: VatBasis,
	asOf: string,
	firstStay: string | null,
	lastStay: string | null,
	// The name of the property or the Property group of the page, for the Export menu and the file names; null = all properties.
	scopeName: string | null,
};

// The last day of a stay month, YYYY-MM-DD.
const lastDayOf = (yearMonth: string): string => {
	const days = new Date(Date.UTC(Number(yearMonth.slice(0, 4)), Number(yearMonth.slice(5, 7)), 0)).getUTCDate();
	return `${yearMonth}-${String(days).padStart(2, '0')}`;
};

const SegmentEvolutionCard: React.FunctionComponent<Props> = ({ companyHotelId, propertyGroupId, ready, includeOptions, period, view, scope, vat, asOf, firstStay, lastStay, scopeName }) => {
	const toaster = useToaster();
	const [exporting, setExporting] = useState(false);
	const [metric, setMetric] = useState<Metric>('roomNights');
	const [chosenGrain, setGrain] = useState<EvolutionGrain>('MONTH');
	// Decision Q5: over WEEK_MAX_MONTHS the chart shows months and Week is locked. The choice changes to Month, so a shorter
	// period later stays on Month until the user clicks Week.
	const weekOff = monthsIn(period) > WEEK_MAX_MONTHS;
	useEffect(() => {
		if (weekOff) setGrain('MONTH');
	}, [weekOff]);
	const grain: EvolutionGrain = weekOff ? 'MONTH' : chosenGrain;
	const evolution = useSegmentEvolution(companyHotelId, propertyGroupId, `${period.from}-01`, lastDayOf(period.to), grain, view, includeOptions, ready);
	const chosen = METRICS.find(m => m.value === metric) ?? METRICS[0];

	// The labels follow the data on the screen, not the control: while a new answer loads, the old data shows dimmed
	// under its own period, grain and view.
	const shown = evolution.data;
	const shownPeriod = formatMonthRange(shown === undefined ? period : { from: shown.stayFrom.slice(0, 7), to: shown.stayTo.slice(0, 7) });
	const shownGrain = shown?.grain ?? grain;
	const shownView = shown?.view ?? view;

	const chart = useMemo(() => {
		const data = evolution.data;
		if (data === undefined) return null;
		const table = tableOf(data, metric, scope, vat);
		const lines = table.lines;
		const series: BarChartSeries[] = lines.map(l => ({ id: l.id, label: l.label, values: l.values, role: l.other ? 'other' : 'series' }));
		// Room nights and Mix % do not change with the switches: no note on them.
		const money = metric === 'revenue' || metric === 'adr';
		const note = money ? missingNote(missingOf(figuresOf(data, lines), scope, vat), 'No bar or no point', 'the period') : null;
		// Light bands over the periods with no imported stay, and a marker at today.
		const bands: BarChartBand[] = [];
		data.periods.forEach((p, i) => {
			if (p.hasData) return;
			const last = bands[bands.length - 1];
			if (last !== undefined && last.to === i - 1) last.to = i;
			else bands.push({ from: i, to: i, label: '' });
		});
		bands.forEach(b => {
			b.label = b.from === 0 && firstStay !== null ? `No data before ${formatDate(firstStay)}` : (lastStay !== null ? `No data after ${formatDate(lastStay)}` : 'No data');
		});
		const today = data.periods.findIndex(p => p.from <= asOf && asOf <= p.to);
		const markers: BarChartMarker[] = today < 0 ? [] : [{ at: today - 0.5 + (Date.parse(asOf) - Date.parse(data.periods[today].from)) / (Date.parse(data.periods[today].to) - Date.parse(data.periods[today].from) + 86400000), label: 'Today' }];
		const columns: DataTableColumn[] = [
			{ id: 'period', label: data.grain === 'MONTH' ? 'Stay month' : 'Stay week from', sortable: false },
			...series.map(s => ({ id: s.id, label: s.label, format: chosen.format, decimals: chosen.decimals, sortable: false })),
		];
		const rows: DataTableRow[] = table.rows.map(r => ({
			id: r.key, cells: Object.fromEntries([['period', r.label], ...series.map((s, i) => [s.id, r.values[i]])]),
		}));
		// The short year on every label (ROO2-246 form C): a period can hold the same month of two years.
		return { categories: data.periods.map(p => `${p.label} \u2019${p.from.slice(2, 4)}`), dates: data.periods.map(p => p.from), series, bands, markers, columns, rows, note };
	}, [evolution.data, metric, scope, vat, asOf, firstStay, lastStay, chosen]);

	// ROO2-280 Export (D1–D3, R2): off while the card loads, shows dimmed old data or an error, and while a file is prepared.
	// A file follows the data on the screen: its stay months, grain and view, the metric tab and the revenue switches (R1).
	const exportReady = shown !== undefined && !evolution.isPlaceholderData && !evolution.isError && !exporting;
	const exportFile = (file: 'CURRENT_VIEW' | 'ALL_DATA') => {
		if (shown === undefined) return;
		setExporting(true);
		const range = { from: shown.stayFrom.slice(0, 7), to: shown.stayTo.slice(0, 7) };
		const request = { companyHotelId, propertyGroupId, stayFrom: range.from, stayTo: range.to, includeOptions };
		const download = file === 'CURRENT_VIEW'
			? BiService.downloadSegmentEvolutionExcel({ ...request, grain: shown.grain, view: shown.view, metric: chosen.input, scope, vat })
			: BiService.downloadSegmentEvolutionData(request);
		const name = file === 'CURRENT_VIEW' ? `Current view ${chosen.label} by ${shown.grain === 'MONTH' ? 'month' : 'week'}` : 'All data';
		download
			.then(blob => saveFile(blob, evolutionFileName(scopeName, name, range, asOf)))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};
	// Direction C of the design canvas: a head with the format and the scope, an icon and a short line per file.
	const exportMenu = <ButtonLinkList toolbar icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={!exportReady}
		description={`Excel · ${scopeName ?? 'All properties'} · ${shownPeriod.replace(/ /g, '\u00a0')}`}
		items={[
			{ name: 'Current view', detail: 'This table, and its data by property', icon: <TableCellsIcon className="h-4 w-4" />, onClick: () => exportFile('CURRENT_VIEW') },
			{ name: 'All data', detail: 'Each stay night, property and PMS segment', icon: <CircleStackIcon className="h-4 w-4" />, onClick: () => exportFile('ALL_DATA') },
		]}>
		{exporting ? 'Preparing…' : 'Export'}
	</ButtonLinkList>;

	const stacked = metric === 'roomNights' || metric === 'revenue';
	// Three different choices, each in its own frame with a caption, as the switches at the top of the page.
	const controls = <div className="mb-4 mt-4 grid grid-cols-2 gap-x-2 gap-y-4 sm:flex sm:flex-wrap sm:items-center sm:gap-x-4">
		<CaptionGroup caption="Metric" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
			<div className="w-full min-w-0">
				<ButtonTabs label="Metric of the evolution chart" options={METRICS} value={metric} onChange={value => setMetric(value as Metric)} />
			</div>
		</CaptionGroup>
		<CaptionGroup caption="Stay period" className="w-full sm:w-auto">
			<div className="w-full min-w-0">
				<ButtonTabs label="Stay period of the evolution chart" value={grain} onChange={value => setGrain(value as EvolutionGrain)}
					options={GRAINS.map(g => g.value === 'WEEK' && weekOff ? { ...g, disabled: true, note: `Week: up to ${WEEK_MAX_MONTHS} months` } : g)} />
			</div>
		</CaptionGroup>
	</div>;
	// Revenue and ADR name the selected revenue (D5): "Revenue (room only incl. VAT)".
	const money = metric === 'revenue' || metric === 'adr';
	const selected = money ? ` (${revenueName(scope, vat).replace(' revenue', '')})` : '';
	const label = `${chosen.label}${selected} by stay ${shownGrain === 'MONTH' ? 'month' : 'week'}, ${shownView === 'GROUPS' ? 'groups' : 'PMS segments'}, ${shownPeriod}`;
	const emptyLabel = chart?.note != null ? 'No value of this revenue in this period: see the note under the chart.' : 'No sold room nights in this period.';
	// Only the first load hides the card body. An error or an empty period keeps the controls, so the user can pick another period.
	const state = chart === null && !evolution.isError ? 'loading' : (evolution.isPlaceholderData ? 'refreshing' : 'default');
	const body = evolution.isError
		? <ChartMessage height={CHART_HEIGHT.normal}>
			<span>The evolution did not load.</span>
			<FormButton size="small" state="white" onClick={() => void evolution.refetch()}>Try again</FormButton>
		</ChartMessage>
		: chart !== null && (stacked
			? <BarChart categories={chart.categories} series={chart.series} format={chosen.format} decimals={chosen.decimals}
				orientation="vertical" stacked bands={chart.bands} markers={chart.markers} ariaLabel={label} emptyLabel={emptyLabel} wholeLabels />
			: <LineChart dates={chart.dates} series={chart.series} format={chosen.format} decimals={chosen.decimals} ariaLabel={label}
				emptyLabel={emptyLabel} axisYear="short" />);

	return <ChartCard title="Segment evolution"
		period={`${shownPeriod} · ${chosen.value === 'adr' ? 'ADR' : chosen.label.toLowerCase()}${selected} by stay ${shownGrain === 'MONTH' ? 'month' : 'week'} · ${shownView === 'GROUPS' ? 'groups' : 'PMS segments'} · past ${shownGrain === 'MONTH' ? 'months' : 'weeks'} actual, later on the books`}
		info={{ title: 'Segment evolution', content: 'The sold room nights of each stay period, by group. The 4 groups with the most total revenue of the period have their own colour; the others add up to “All other”. Room nights and revenue stack; ADR and Mix % are lines. The revenue switches and the stay months at the top of the page apply to the chart and the table.' }}
		caption={chart?.note ?? undefined}
		state={state}
		actions={exportMenu}
		table={chart === null || evolution.isError ? undefined : <>{controls}<DataTable columns={chart.columns} rows={chart.rows} ariaLabel={label} framed={false} size="normal" /></>}>
		{controls}
		{body}
	</ChartCard>;
};

export default SegmentEvolutionCard;
